import { IVector3 } from '../api.js';
import { vector3_pool } from '../Math.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
export class TriangleCustomMesh {
    constructor(positions = null, indices = null, colors = null, normals = null, tangents = null, uv0 = null, uv1 = null) {
        this.positions = positions;
        this.indices = indices;
        this.colors = colors;
        this.normals = normals;
        this.tangents = tangents;
        this.uv0 = uv0;
        this.uv1 = uv1;
        this.verts_per_face = 3;
    }
    create_mesh(viewer, material) {
        return viewer.create_custom_mesh(this.get_mesh_description(), material);
    }
    get_mesh_description() {
        if (this.positions === null || this.indices === null) {
            throw Error('TriangleCustomMesh: mesh description can not be generated for meshes without positions and indices');
        }
        if (this.verts_per_face !== 3) {
            throw Error('get_mesh_description needs to be overridden with a solution that triangulates if verts_per_face is not 3');
        }
        return {
            positions: this.positions,
            indices: this.indices,
            colors: this.colors,
            normals: this.normals,
            tangents: this.tangents,
            uv0: this.uv0,
            uv1: this.uv1
        };
    }
    generate_simple_index_buffer() {
        var _a, _b;
        const num_indices = ((_b = (_a = this.positions) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) / 3;
        const indices = num_indices > 65535 ? new Uint32Array(num_indices) : new Uint16Array(num_indices);
        for (let i = 0; i < num_indices; i++) {
            indices[i] = i;
        }
        this.indices = indices;
    }
    generate_vertex_normals() {
        if (this.indices === null || this.indices.length < 3) {
            console.error('Only meshes with indices can generate normals');
            return false;
        }
        if (this.positions === null) {
            console.error('Mesh does not have any positions to generate normals from');
            return false;
        }
        if (this.normals === null) {
            this.normals = new Float32Array(this.positions.length);
        }
        for (let i = 0; i < this.normals.length; i++) {
            this.normals[i] = 0.0;
        }
        const vertex0 = vector3_pool.fetch();
        const vertex1 = vector3_pool.fetch();
        const vertex2 = vector3_pool.fetch();
        const edge0 = vector3_pool.fetch();
        const edge1 = vector3_pool.fetch();
        const normal = vector3_pool.fetch();
        for (let i = 0; i < this.indices.length; i += this.verts_per_face) {
            vertex0.copyFromFloats(this.positions[this.indices[i + 0] * 3 + 0], this.positions[this.indices[i + 0] * 3 + 1], this.positions[this.indices[i + 0] * 3 + 2]);
            vertex1.copyFromFloats(this.positions[this.indices[i + 1] * 3 + 0], this.positions[this.indices[i + 1] * 3 + 1], this.positions[this.indices[i + 1] * 3 + 2]);
            vertex2.copyFromFloats(this.positions[this.indices[i + 2] * 3 + 0], this.positions[this.indices[i + 2] * 3 + 1], this.positions[this.indices[i + 2] * 3 + 2]);
            vertex0.subtractToRef(vertex1, edge0);
            vertex0.subtractToRef(vertex2, edge1);
            BABYLON_Vector3.CrossToRef(edge0, edge1, normal);
            normal.normalize();
            const weights = [
                Math.acos(IVector3.dot(vertex1.subtractToRef(vertex0, edge0).normalize(), vertex2.subtractToRef(vertex0, edge1).normalize())),
                Math.acos(IVector3.dot(vertex0.subtractToRef(vertex1, edge0).normalize(), vertex2.subtractToRef(vertex1, edge1).normalize())),
                Math.acos(IVector3.dot(vertex0.subtractToRef(vertex2, edge0).normalize(), vertex1.subtractToRef(vertex2, edge1).normalize()))
            ];
            for (let j = 0; j < 3; j++) {
                this.normals[this.indices[i + j] * 3 + 0] += normal.x * weights[j];
                this.normals[this.indices[i + j] * 3 + 1] += normal.y * weights[j];
                this.normals[this.indices[i + j] * 3 + 2] += normal.z * weights[j];
            }
        }
        vector3_pool.return(vertex0, vertex1, vertex2, edge0, edge1, normal);
        for (let i = 0; i < this.normals.length; i += 3) {
            const length = Math.sqrt(this.normals[i + 0] * this.normals[i + 0] +
                this.normals[i + 1] * this.normals[i + 1] +
                this.normals[i + 2] * this.normals[i + 2]);
            this.normals[i + 0] /= length;
            this.normals[i + 1] /= length;
            this.normals[i + 2] /= length;
        }
        return true;
    }
    generate_face_normals() {
        if (this.indices === null || this.indices.length < 3) {
            console.error('Only meshes with indices can generate normals');
            return false;
        }
        if (this.positions === null) {
            console.error('Mesh does not have any positions to generate normals from');
            return false;
        }
        const num_faces = Math.floor(this.indices.length / this.verts_per_face);
        const num_verts = num_faces * this.verts_per_face;
        const new_indices = this.indices.slice();
        // Here we remove tangents, since we are generating normals, tangents won't match anyway
        this.tangents = null;
        const new_positions = new Float32Array(num_verts * 3); // 3 values per vert, XYZ
        const new_normals = new Float32Array(num_verts * 3); // 3 values per vert, XYZ
        const new_colors = this.colors ? new Float32Array(num_verts * 4) : null; // 4 values per vert, RGBA
        const new_uv0 = this.uv0 ? new Float32Array(num_verts * 2) : null; // 2 values per vert, UV
        const new_uv1 = this.uv1 ? new Float32Array(num_verts * 2) : null; // 2 values per vert, UV
        let index_counter = 0;
        const vertex0 = vector3_pool.fetch();
        const vertex1 = vector3_pool.fetch();
        const vertex2 = vector3_pool.fetch();
        const edge0 = vector3_pool.fetch();
        const edge1 = vector3_pool.fetch();
        const normal = vector3_pool.fetch();
        const copy_buffer_data = (dest, src, dest_offset, src_indices, src_index, num_values) => {
            // Number of values per field i.e. 3 per position or 2 per uv
            for (let i = 0; i < num_values; i++) {
                // We always copy for each triangle
                for (let j = 0; j < this.verts_per_face; j++) {
                    dest[(dest_offset + j) * num_values + i] = src[src_indices[src_index + j] * num_values + i];
                }
            }
        };
        for (let i = 0; i < this.indices.length; i += this.verts_per_face) {
            vertex0.copyFromFloats(this.positions[this.indices[i + 0] * 3 + 0], this.positions[this.indices[i + 0] * 3 + 1], this.positions[this.indices[i + 0] * 3 + 2]);
            vertex1.copyFromFloats(this.positions[this.indices[i + 1] * 3 + 0], this.positions[this.indices[i + 1] * 3 + 1], this.positions[this.indices[i + 1] * 3 + 2]);
            vertex2.copyFromFloats(this.positions[this.indices[i + 2] * 3 + 0], this.positions[this.indices[i + 2] * 3 + 1], this.positions[this.indices[i + 2] * 3 + 2]);
            vertex0.subtractToRef(vertex1, edge0);
            vertex0.subtractToRef(vertex2, edge1);
            BABYLON_Vector3.CrossToRef(edge0, edge1, normal);
            normal.normalize();
            const new_index = index_counter;
            index_counter += 3;
            new_indices[i + 0] = new_index + 0;
            new_indices[i + 1] = new_index + 1;
            new_indices[i + 2] = new_index + 2;
            for (let j = 0; j < this.verts_per_face; j++) {
                new_normals[(new_index + j) * 3 + 0] = normal.x;
                new_normals[(new_index + j) * 3 + 1] = normal.y;
                new_normals[(new_index + j) * 3 + 2] = normal.z;
            }
            // Expand old data buffers
            copy_buffer_data(new_positions, this.positions, new_index, this.indices, i, 3);
            if (new_colors) {
                copy_buffer_data(new_colors, this.colors, new_index, this.indices, i, 4);
            }
            if (new_uv0) {
                copy_buffer_data(new_uv0, this.uv0, new_index, this.indices, i, 2);
            }
            if (new_uv1) {
                copy_buffer_data(new_uv1, this.uv1, new_index, this.indices, i, 2);
            }
        }
        this.positions = new_positions;
        this.indices = new_indices;
        this.colors = new_colors;
        this.normals = new_normals;
        this.uv0 = new_uv0;
        this.uv1 = new_uv1;
        vector3_pool.return(vertex0, vertex1, vertex2, edge0, edge1, normal);
        return true;
    }
}
export class QuadCustomMesh {
    constructor(positions = null, indices = null, colors = null, normals = null, tangents = null, uv0 = null, uv1 = null) {
        this.positions = positions;
        this.indices = indices;
        this.colors = colors;
        this.normals = normals;
        this.tangents = tangents;
        this.uv0 = uv0;
        this.uv1 = uv1;
        this.verts_per_face = 4;
    }
    generate_simple_index_buffer() {
        var _a, _b;
        const num_indices = ((_b = (_a = this.positions) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) / 3;
        const indices = num_indices > 65535 ? new Uint32Array(num_indices) : new Uint16Array(num_indices);
        for (let i = 0; i < num_indices; i++) {
            indices[i] = i;
        }
        this.indices = indices;
    }
    create_mesh(viewer, material) {
        return viewer.create_custom_mesh(this.get_mesh_description(), material);
    }
    get_mesh_description() {
        if (this.positions === null || this.indices === null) {
            throw Error('QuadCustomMesh: mesh description can not be generated for meshes without positions and indices');
        }
        if (this.indices === null) {
            this.generate_simple_index_buffer();
        }
        // indices are not null here
        const quad_indices = this.indices;
        const triangle_indices = quad_indices instanceof Uint16Array
            ? new Uint16Array(quad_indices.length * 1.5)
            : new Uint32Array(quad_indices.length * 1.5);
        for (let i = 0; i < quad_indices.length / 4; i++) {
            triangle_indices[i * 6 + 0] = quad_indices[i * 4 + 0];
            triangle_indices[i * 6 + 1] = quad_indices[i * 4 + 1];
            triangle_indices[i * 6 + 2] = quad_indices[i * 4 + 2];
            triangle_indices[i * 6 + 3] = quad_indices[i * 4 + 0];
            triangle_indices[i * 6 + 4] = quad_indices[i * 4 + 2];
            triangle_indices[i * 6 + 5] = quad_indices[i * 4 + 3];
        }
        return {
            positions: this.positions,
            indices: triangle_indices,
            colors: this.colors,
            normals: this.normals,
            tangents: this.tangents,
            uv0: this.uv0,
            uv1: this.uv1
        };
    }
    generate_face_normals() {
        if (this.indices === null || this.indices.length < 3) {
            console.error('Only meshes with indices can generate normals');
            return false;
        }
        if (this.positions === null) {
            console.error('Mesh does not have any positions to generate normals from');
            return false;
        }
        const num_faces = Math.floor(this.indices.length / this.verts_per_face);
        const num_verts = num_faces * this.verts_per_face;
        const new_indices = this.indices.slice();
        // Here we remove tangents, since we are generating normals, tangents won't match anyway
        this.tangents = null;
        const new_positions = new Float32Array(num_verts * 3); // 3 values per vert
        const new_normals = new Float32Array(num_verts * 3); // 3 values per vert
        const new_colors = this.colors ? new Float32Array(num_verts * 4) : null; // 4 values per vert, RGBA
        const new_uv0 = this.uv0 ? new Float32Array(num_verts * 2) : null; // 2 values per vert
        const new_uv1 = this.uv1 ? new Float32Array(num_verts * 2) : null; // 2 values per vert
        let index_counter = 0;
        const vertex0 = vector3_pool.fetch();
        const vertex1 = vector3_pool.fetch();
        const vertex2 = vector3_pool.fetch();
        const vertex3 = vector3_pool.fetch();
        const edge0 = vector3_pool.fetch();
        const edge1 = vector3_pool.fetch();
        const normal = vector3_pool.fetch();
        const copy_buffer_data = (dest, src, dest_offset, src_indices, src_index, num_values) => {
            // Number of values per field i.e. 3 per position or 2 per uv
            for (let i = 0; i < num_values; i++) {
                // We always copy for each quad
                for (let j = 0; j < this.verts_per_face; j++) {
                    dest[(dest_offset + j) * num_values + i] = src[src_indices[src_index + j] * num_values + i];
                }
            }
        };
        for (let i = 0; i < this.indices.length; i += this.verts_per_face) {
            vertex0.copyFromFloats(this.positions[this.indices[i + 0] * 3 + 0], this.positions[this.indices[i + 0] * 3 + 1], this.positions[this.indices[i + 0] * 3 + 2]);
            vertex1.copyFromFloats(this.positions[this.indices[i + 1] * 3 + 0], this.positions[this.indices[i + 1] * 3 + 1], this.positions[this.indices[i + 1] * 3 + 2]);
            vertex2.copyFromFloats(this.positions[this.indices[i + 2] * 3 + 0], this.positions[this.indices[i + 2] * 3 + 1], this.positions[this.indices[i + 2] * 3 + 2]);
            vertex3.copyFromFloats(this.positions[this.indices[i + 3] * 3 + 0], this.positions[this.indices[i + 3] * 3 + 1], this.positions[this.indices[i + 3] * 3 + 2]);
            vertex0.subtractToRef(vertex2, edge0);
            vertex3.subtractToRef(vertex1, edge1);
            BABYLON_Vector3.CrossToRef(edge0, edge1, normal);
            normal.normalize();
            const new_index = index_counter;
            index_counter += 3;
            new_indices[i + 0] = new_index + 0;
            new_indices[i + 1] = new_index + 1;
            new_indices[i + 2] = new_index + 2;
            for (let j = 0; j < 4; j++) {
                new_normals[(new_index + j) * 3 + 0] = normal.x;
                new_normals[(new_index + j) * 3 + 1] = normal.y;
                new_normals[(new_index + j) * 3 + 2] = normal.z;
            }
            // Expand old data buffers
            copy_buffer_data(new_positions, this.positions, new_index, this.indices, i, 3);
            if (new_colors) {
                copy_buffer_data(new_colors, this.colors, new_index, this.indices, i, 4);
            }
            if (new_uv0) {
                copy_buffer_data(new_uv0, this.uv0, new_index, this.indices, i, 2);
            }
            if (new_uv1) {
                copy_buffer_data(new_uv1, this.uv1, new_index, this.indices, i, 2);
            }
        }
        this.positions = new_positions;
        this.indices = new_indices;
        this.colors = new_colors;
        this.normals = new_normals;
        this.uv0 = new_uv0;
        this.uv1 = new_uv1;
        vector3_pool.return(vertex0, vertex1, vertex2, vertex3, edge0, edge1, normal);
        return true;
    }
}
export function generate_index_buffer(num_indices) {
    const indices = num_indices > 65535 ? new Uint32Array(num_indices) : new Uint16Array(num_indices);
    for (let i = 0; i < num_indices; i++) {
        indices[i] = i;
    }
    return indices;
}
/**
 * Generic method for slicing a buffer from start and length while keeping narrow types
 * It will clamp start and len so that slice is not out of bounds
 * @param buffer the buffer to get slice from
 * @param start the first index on @buffer to use
 * @param length the number of elements from @start for the slice, shorter slice is given if out of bounds.
 * @returns New array of same type as @buffer with the slice specified from @start and @length
 */
function get_buffer_slice(buffer, start, length) {
    // Slice handles out of bounds gracefully but we still clamp negative values
    start = start >= 0 ? start : 0;
    length = length >= 0 ? length : buffer.length;
    return buffer.slice(start, start + length);
}
function generate_submesh_buffers(indices, positions, colors, normals, tangents, uv0, uv1) {
    // Make a copy of the same size
    const new_indices = indices.slice();
    const old_index_to_new_index = new Map();
    let new_index = 0;
    for (let i = 0; i < indices.length; i++) {
        const old_index = indices[i];
        if (!old_index_to_new_index.has(old_index)) {
            old_index_to_new_index.set(old_index, new_index);
            new_index++;
        }
        new_indices[i] = old_index_to_new_index.get(old_index);
    }
    const new_positions = new Float32Array(old_index_to_new_index.size * 3);
    const new_colors = colors ? new Float32Array(old_index_to_new_index.size * 4) : null;
    const new_normals = normals ? new Float32Array(old_index_to_new_index.size * 3) : null;
    const new_tangents = tangents ? new Float32Array(old_index_to_new_index.size * 3) : null;
    const new_uv0 = uv0 ? new Float32Array(old_index_to_new_index.size * 2) : null;
    const new_uv1 = uv1 ? new Float32Array(old_index_to_new_index.size * 2) : null;
    for (const [old_index, new_index] of old_index_to_new_index) {
        new_positions[new_index * 3 + 0] = positions[old_index * 3 + 0];
        new_positions[new_index * 3 + 1] = positions[old_index * 3 + 1];
        new_positions[new_index * 3 + 2] = positions[old_index * 3 + 2];
        if (new_colors && colors) {
            new_colors[new_index * 4 + 0] = colors[old_index * 4 + 0];
            new_colors[new_index * 4 + 1] = colors[old_index * 4 + 1];
            new_colors[new_index * 4 + 2] = colors[old_index * 4 + 2];
            new_colors[new_index * 4 + 3] = colors[old_index * 4 + 3];
        }
        if (new_normals && normals) {
            new_normals[new_index * 3 + 0] = normals[old_index * 3 + 0];
            new_normals[new_index * 3 + 1] = normals[old_index * 3 + 1];
            new_normals[new_index * 3 + 2] = normals[old_index * 3 + 2];
        }
        if (new_tangents && tangents) {
            new_tangents[new_index * 3 + 0] = tangents[old_index * 3 + 0];
            new_tangents[new_index * 3 + 1] = tangents[old_index * 3 + 1];
            new_tangents[new_index * 3 + 2] = tangents[old_index * 3 + 2];
        }
        if (new_uv0 && uv0) {
            new_uv0[new_index * 2 + 0] = uv0[old_index * 2 + 0];
            new_uv0[new_index * 2 + 1] = uv0[old_index * 2 + 1];
        }
        if (new_uv1 && uv1) {
            new_uv1[new_index * 2 + 0] = uv1[old_index * 2 + 0];
            new_uv1[new_index * 2 + 1] = uv1[old_index * 2 + 1];
        }
    }
    const vertex_data = {
        indices: new_indices,
        colors: new_colors,
        positions: new_positions,
        normals: new_normals,
        tangents: new_tangents,
        uv0: new_uv0,
        uv1: new_uv1
    };
    return vertex_data;
}
export function split_mesh_on_material(mesh_description, material_ranges) {
    const objects = [];
    for (let i = 0; i < material_ranges.length; i++) {
        const material_range = material_ranges[i];
        objects.push({
            mesh_description: generate_submesh_buffers(get_buffer_slice(mesh_description.indices, material_range.first_index, material_range.num_indices), mesh_description.positions, mesh_description.colors, mesh_description.normals, mesh_description.tangents, mesh_description.uv0, mesh_description.uv1),
            material: material_range.material
        });
    }
    return objects;
}
//# sourceMappingURL=CustomMesh.js.map